import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";

import { useForcedTheme } from "@/lib/hooks/use-forced-theme";
import { useNoIndexMeta } from "@/lib/hooks/use-noindex-meta";
import { tLocalized, type LocalizedString } from "@/types/localized-string";

/**
 * Public waiting room display — designed for clinic TVs.
 * No auth, no nav, full-screen. Ported 1:1 from legacy
 * `src/app/display/[facilityId]/page.tsx` + `waiting-room-screen.tsx`.
 *
 * Query params:
 *   rooms  — comma-separated room filter (e.g. "Cabinet 1,Cabinet 2")
 *   theme  — "dark" (default) or "light"
 *   fontSize — "sm", "md" (default), or "lg"
 *
 * Example: /display/abc-123?rooms=Cabinet%201&theme=dark&fontSize=lg
 */

interface RoomQueue {
  roomName: string | null;
  /** `patientName` is first name + last initial — the API masks it. */
  nowServing: { queueNumber: number; patientName: string } | null;
  next: { queueNumber: number; patientName: string } | null;
  waitingCount: number;
}

interface QueueData {
  facilityId: string;
  facilityName: LocalizedString | null;
  rooms: RoomQueue[];
  totalWaiting: number;
  avgServeMinutes: number;
  estimatedWaitMinutes: number;
}

type DisplayFontSize = "sm" | "md" | "lg";

interface DisplaySearchParams {
  rooms?: string;
  theme: "dark" | "light";
  fontSize: DisplayFontSize;
}

const fontSizeClasses = {
  sm: {
    number: "text-6xl",
    name: "text-2xl",
    label: "text-lg",
    room: "text-xl",
    footer: "text-lg",
  },
  md: {
    number: "text-8xl",
    name: "text-3xl",
    label: "text-xl",
    room: "text-2xl",
    footer: "text-xl",
  },
  lg: {
    number: "text-9xl",
    name: "text-4xl",
    label: "text-2xl",
    room: "text-3xl",
    footer: "text-2xl",
  },
};

export const Route = createFileRoute("/display/$facilityId/")({
  validateSearch: (search: Record<string, unknown>): DisplaySearchParams => {
    const roomsRaw = typeof search.rooms === "string" && search.rooms ? search.rooms : undefined;
    const fontSizeRaw = typeof search.fontSize === "string" ? search.fontSize : undefined;
    return {
      rooms: roomsRaw,
      theme: search.theme === "light" ? "light" : "dark",
      fontSize: (["sm", "md", "lg"].includes(fontSizeRaw ?? "")
        ? fontSizeRaw
        : "md") as DisplayFontSize,
    };
  },
  component: WaitingRoomDisplayPage,
});

function WaitingRoomDisplayPage() {
  const { facilityId } = Route.useParams();
  const { rooms, theme, fontSize } = Route.useSearch();
  const { t, i18n } = useLingui();

  useNoIndexMeta();
  useForcedTheme(theme);

  const [data, setData] = useState<QueueData | null>(null);
  // `null` = no error; `0` = connection lost; otherwise the HTTP status.
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const sizes = fontSizeClasses[fontSize];

  // Poll queue data: immediate fetch + 10s interval
  useEffect(() => {
    let active = true;

    async function fetchQueue() {
      try {
        const qs = rooms ? `?rooms=${encodeURIComponent(rooms)}` : "";
        const json = await apiFetch.get<QueueData>(`/api/facilities/${facilityId}/queue${qs}`, {
          skipAuthRefresh: true,
        });
        if (!active) return;
        setData(json);
        setErrorStatus(null);
        setLastUpdated(new Date());
      } catch (err) {
        if (!active) return;
        setErrorStatus(err instanceof ApiError ? err.status : 0);
      }
    }

    void fetchQueue();
    const interval = setInterval(() => void fetchQueue(), 10_000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [facilityId, rooms]);

  if (errorStatus !== null && !data) {
    return (
      <div className="bg-background text-foreground flex min-h-dvh items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="text-warning mx-auto mb-4 size-16" aria-hidden />
          <p className="text-2xl">
            <ErrorLabel status={errorStatus} />
          </p>
          <p className="text-muted-foreground mt-2">
            <Trans>Retrying automatically...</Trans>
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-background text-foreground flex min-h-dvh items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-16 w-16 animate-spin rounded-full border-4 border-t-transparent" />
          <p className="text-muted-foreground mt-6 text-xl">
            <Trans>Loading queue...</Trans>
          </p>
        </div>
      </div>
    );
  }

  const roomsToShow =
    data.rooms.length > 0
      ? data.rooms
      : [{ roomName: null, nowServing: null, next: null, waitingCount: 0 }];
  const totalWaiting = data.totalWaiting;
  const estimatedWaitMinutes = data.estimatedWaitMinutes;
  const updatedTime = lastUpdated
    ? lastUpdated.toLocaleTimeString(i18n.locale, { hour: "2-digit", minute: "2-digit" })
    : null;
  // The screen belongs to the clinic: its own name on top, PontiMed only as a
  // small credit in the footer.
  const facilityName = tLocalized(data.facilityName, i18n.locale);

  return (
    <div className="bg-background text-foreground flex min-h-dvh flex-col p-6">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className={`${sizes.room} font-bold tracking-tight`}>{facilityName || "PontiMed"}</h1>
        </div>
        <div className={`text-muted-foreground ${sizes.label}`}>
          <Trans>{totalWaiting} waiting</Trans>
        </div>
      </div>

      {/* Room Cards Grid */}
      <div
        className="grid flex-1 auto-rows-fr gap-6"
        style={{
          gridTemplateColumns: `repeat(${Math.min(roomsToShow.length, 3)}, minmax(0, 1fr))`,
        }}
      >
        <AnimatePresence mode="popLayout">
          {roomsToShow.map((room, idx) => {
            const waitingCount = room.waitingCount;
            return (
              <m.div
                key={room.roomName ?? `room-${idx}`}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
                className="bg-card border-border flex flex-col rounded-2xl border-2 p-6"
              >
                {/* Room name */}
                {room.roomName && (
                  <div
                    className={`${sizes.room} text-muted-foreground mb-6 font-semibold tracking-wider uppercase`}
                  >
                    {room.roomName}
                  </div>
                )}

                {/* Now Serving */}
                <div className="bg-primary-soft border-primary mb-4 flex flex-1 flex-col items-center justify-center rounded-xl border-2 p-6">
                  <span
                    className={`${sizes.label} text-primary mb-2 font-medium tracking-widest uppercase`}
                  >
                    <Trans>Now Serving</Trans>
                  </span>
                  {room.nowServing ? (
                    <m.div
                      key={room.nowServing.queueNumber}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center"
                    >
                      <span className={`${sizes.number} text-primary font-black tabular-nums`}>
                        #{room.nowServing.queueNumber}
                      </span>
                      <p className={`${sizes.name} text-foreground mt-2 font-medium`}>
                        {room.nowServing.patientName}
                      </p>
                    </m.div>
                  ) : (
                    <p className={`${sizes.label} text-muted-foreground text-center`}>
                      <Trans>Your number will appear on this screen.</Trans>
                    </p>
                  )}
                </div>

                {/* Next Up */}
                <div className="bg-accent-soft border-accent flex items-center justify-between rounded-xl border p-4">
                  <span
                    className={`${sizes.label} text-accent-strong font-medium tracking-wider uppercase`}
                  >
                    {t({ message: "Next", context: "queue" })}
                  </span>
                  {room.next ? (
                    <div className="text-right">
                      <span className="text-accent-strong text-4xl font-bold tabular-nums">
                        #{room.next.queueNumber}
                      </span>
                      <span className={`ml-3 ${sizes.label} text-muted-foreground`}>
                        {room.next.patientName}
                      </span>
                    </div>
                  ) : (
                    <span className={`${sizes.label} text-muted-foreground`}>
                      <Trans>No one waiting</Trans>
                    </span>
                  )}
                </div>

                {/* Waiting count */}
                <div className={`text-muted-foreground mt-3 text-center ${sizes.label}`}>
                  <Trans>{waitingCount} in queue</Trans>
                </div>
              </m.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div
        className={`text-muted-foreground mt-6 flex items-center justify-between ${sizes.footer}`}
      >
        <div>
          <Trans>Est. wait: ~{estimatedWaitMinutes} min</Trans>
        </div>
        {errorStatus !== null && (
          <div className="text-warning inline-flex items-center gap-2">
            <AlertTriangle className="size-5" aria-hidden />
            <ErrorLabel status={errorStatus} /> — <Trans>retrying...</Trans>
          </div>
        )}
        <div className="text-right">
          {updatedTime ? <Trans>Updated {updatedTime}</Trans> : ""}
          {facilityName ? <span className="ml-3 text-sm opacity-70">PontiMed</span> : null}
        </div>
      </div>
    </div>
  );
}

function ErrorLabel({ status }: { status: number }) {
  if (status === 0) return <Trans>Connection lost</Trans>;
  return <Trans>Error {status}</Trans>;
}
